<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="modal-backdrop" @click.self="close">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title">

        <header class="modal-card-header">
          <div class="header-icon">
            <img alt="Project estimator icon" src="img/project-estimator-logo-positive.svg"/>
          </div>
          <div class="header-text">
            <h2 id="modal-title" class="modal-title">Marketing Preferences</h2>
          </div>
        </header>

        <div class="modal-card-body">
          <p class="intro-text">
            Choose how you'd like to hear from us. We'd love to keep you updated with the latest offers, news, and updates.
            You can change these at any time.
          </p>

          <div class="preference-list">
            <!-- Email -->
            <label class="preference-item" :class="{ 'is-checked': preferences.email }">
              <div class="preference-icon email-icon">
                <fa icon="envelope"></fa>
              </div>
              <div class="preference-info">
                <span class="preference-label">Email</span>
                <span class="preference-desc">Receive newsletters, promotions, and account updates</span>
              </div>
              <div class="preference-control">
                <b-checkbox v-model="preferences.email" />
              </div>
            </label>

            <!-- SMS -->
            <label class="preference-item" :class="{ 'is-checked': preferences.sms }">
              <div class="preference-icon sms-icon">
                <fa icon="comment-dots"></fa>
              </div>
              <div class="preference-info">
                <span class="preference-label">SMS</span>
                <span class="preference-desc">Get text alerts for time-sensitive offers and reminders</span>
              </div>
              <div class="preference-control">
                <b-checkbox v-model="preferences.sms" />
              </div>
            </label>
          </div>

          <p class="essential-note">
            You'll still receive essential service emails, such as account and security updates, regardless of your preferences above.
          </p>
        </div>

        <footer class="modal-card-footer">
          <button class="btn-secondary" @click="close">Cancel</button>
          <button class="btn-primary" @click="save">Save Preferences</button>
        </footer>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'MarketingPreferencesModal',

  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      preferences: {
        email: false,
        sms: false,
      },
    };
  },

  computed: {
    loggedInUser() {
      return this.$store.getters['user/loggedInUser'];
    },
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.preferences.email = this.loggedInUser && this.loggedInUser.preferences && this.loggedInUser.preferences.marketing && this.loggedInUser.preferences.marketing.email || false;
        this.preferences.sms = this.loggedInUser && this.loggedInUser.preferences && this.loggedInUser.preferences.marketing && this.loggedInUser.preferences.marketing.sms || false;
      }
    },
  },

  methods: {
    close() {
      this.clearForm();
      this.$emit('close');
    },
    save() {
      this.$store.dispatch('user/updateMarketingPreference', {
        user: this.loggedInUser,
        smsPreference: this.preferences.sms,
        emailPreference: this.preferences.email,
      }).then(() => {
        this.$buefy.toast.open({
          message: 'Marketing preferences updated successfully',
          type: 'is-success',
          pauseOnHover: true,
        });
        this.close();
      }, () => {
        this.$buefy.toast.open({
          message: 'Failed to update marketing preferences',
          type: 'is-danger',
          pauseOnHover: true,
        });
      });
    },
    clearForm() {
      this.preferences.email = false;
      this.preferences.sms = false;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Backdrop ─────────────────────────────────────────────── */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 14, 26, 0.65);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

/* ── Card ─────────────────────────────────────────────────── */
.modal-card {
  background: $white;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(10, 14, 26, 0.18);
  display: flex;
  flex-direction: column;
}

/* ── Header ───────────────────────────────────────────────── */
.modal-card-header {
  background-color: $primary;
  color: $primary-font-color;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid $grey;
  position: sticky;
  top: 0;
  z-index: 1;
}

.header-text {
  margin-left: 40px;
}

.header-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 40px;
    width: auto;
    object-fit: contain;
  }
}

.modal-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: $primary-font-color;
  margin: 0;
  line-height: 1.2;
}

.modal-subtitle {
  font-size: 0.78rem;
  color: $dark;
  margin: 2px 0 0;
}

/* ── Body ─────────────────────────────────────────────────── */
.modal-card-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.intro-text {
  font-size: 0.875rem;
  color: $dark;
  line-height: 1.55;
  margin: 0;
}

.essential-note {
  font-size: 0.775rem;
  color: $dark;
  line-height: 1.5;
  margin: 0;
}

/* ── Preference items ─────────────────────────────────────── */
.preference-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.preference-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.9rem 1rem;
  border: 1.5px solid $dark;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}

.preference-item:hover {
  border-color: $accent;
}

.preference-item.is-checked {
  border-color: $accent;
  background: #eff6ff;
}

.preference-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-icon {
  background: $email-icon-background;
  color: $email-icon-orange;
}

.sms-icon {
  background: $sms-icon-background;
  color: $green;

}

.preference-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.preference-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: $primary-font-color;
}

.preference-desc {
  font-size: 0.775rem;
  color: $grey;
  line-height: 1.4;
}

.preference-control {
  flex-shrink: 0;
}

/* ── Footer ───────────────────────────────────────────────── */
.modal-card-footer {
  background-color: $primary;
  color: $primary-font-color;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid $grey;
  position: sticky;
  bottom: 0;
}

.btn-secondary,
.btn-primary {
  padding: 0.5rem 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 7px;
  cursor: pointer;
  border: none;
  transition: background 0.15s, box-shadow 0.15s;
}

.btn-secondary {
  background: $white;
  color: $primary-font-color;
  border: 1.5px solid $grey;
}

.btn-primary {
  background: $accent;
  color: $white;
  border: 1.5px solid $grey;
}
.btn-primary:hover {
  background: $accent;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35);
}

/* ── Transition ───────────────────────────────────────────── */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-active .modal-card,
.modal-fade-leave-active .modal-card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .modal-card,
.modal-fade-leave-to .modal-card {
  transform: translateY(12px);
  opacity: 0;
}
</style>
